import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { openSuccessionDrawer } from '../../actions/successionDrawerActionCreators';
import { S3Image, getAvatarStyle } from '../../lib/s3';

// Polymet-style advisor card adapted to existing data
// Reference: tasks/prototypes/polymet-home-page-redesign/src/polymet/components/advisor-card.tsx

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '' || Number(value) === 0) return 'N/A';
  const num = Number(value);
  if (Number.isNaN(num)) return 'N/A';
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `$${(num / 1_000).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
};

const AdvisorCard = ({ user, className }) => {
  const dispatch = useDispatch();
  const name = user.allowed_name || user.user_name;
  const location = [user.city, user.province].filter(Boolean).join(', ');
  const specialties = (user.primary_markets && user.primary_markets.length > 0)
    ? user.primary_markets
    : (user.specialized_businesses || []);

  const handleClick = () => {
    dispatch(openSuccessionDrawer(user.fb_user_name || user.user_name));
  };

  const avatarUrl = user.profile_picture ? getAvatarStyle(user.profile_picture).url : undefined;

  return (
    <div
      className={[
        'bg-white rounded-xl border border-gray-200 p-4 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02]',
        className || ''
      ].join(' ')}
      onClick={handleClick}
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="relative">
          {avatarUrl ? (
            <img src={avatarUrl} alt={name} className="w-12 h-12 rounded-full object-cover" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold">
              {String(name || '?').charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">{name}</h3>
          <div className="flex items-center gap-1 mt-1">
            <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4.5 8-10a8 8 0 10-16 0c0 5.5 8 10 8 10z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <p className="text-sm text-gray-600 truncate">{location || '—'}</p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="text-center p-2 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-center gap-1 mb-1">
            <svg className="w-3 h-3 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 17l6-6 4 4 8-8" />
            </svg>
            <span className="text-xs text-gray-600">Production</span>
          </div>
          <div className="text-sm font-semibold text-gray-900">{formatCurrency(user.gross_production)}</div>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-center gap-1 mb-1">
            <svg className="w-3 h-3 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 8c-1.7 0-3 .9-3 2s1.3 2 3 2 3 .9 3 2-1.3 2-3 2" />
            </svg>
            <span className="text-xs text-gray-600">AUM</span>
          </div>
          <div className="text-sm font-semibold text-gray-900">{formatCurrency(user.assets_under_management)}</div>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-center gap-1 mb-1">
            <svg className="w-3 h-3 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 00-3-3.87" />
              <path d="M16 3.13a4 4 0 010 7.75" />
            </svg>
            <span className="text-xs text-gray-600">Clients</span>
          </div>
          <div className="text-sm font-semibold text-gray-900">{user.number_of_clients ?? 'N/A'}</div>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-center gap-1 mb-1">
            <svg className="w-3 h-3 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 8v4l3 3M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs text-gray-600">Years</span>
          </div>
          <div className="text-sm font-semibold text-gray-900">{user.years_practicing ?? 'N/A'}</div>
        </div>
      </div>

      {/* Specialties */}
      {specialties && specialties.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {specialties.slice(0, 2).map((s) => (
              <span key={s} className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-700 bg-gray-50">{s}</span>
            ))}
            {specialties.length > 2 && (
              <span className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-700 bg-white">+{specialties.length - 2}</span>
            )}
          </div>
        </div>
      )}

      {/* Action Button */}
      <button
        type="button"
        className="w-full bg-blue-100 hover:bg-blue-200 text-blue-700 border border-blue-200 rounded-md py-2 text-sm font-medium"
        onClick={(e) => { e.stopPropagation(); dispatch(openSuccessionDrawer(user.fb_user_name || user.user_name)); }}
      >
        <span className="inline-flex items-center">
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
          Connect
        </span>
      </button>
    </div>
  );
};

AdvisorCard.propTypes = {
  user: PropTypes.object.isRequired,
  className: PropTypes.string,
};

export default AdvisorCard;


